// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators compare three yearly spend totals with the same
 * twelve monthly sums in this card. Plain proportional bars provide that small
 * fixed chart without adding a charting dependency, its bundle weight, or a
 * second formatting system solely for twelve non-interactive values.
 */
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Tallest bar height in px inside the 150px chart area, leaving room for value labels
const MAX_BAR_HEIGHT = 104

interface CostSummaryCardProps {
  year: number
  monthSums: number[]
  serviceCount: number
  /** 1-based month, only set when viewing the current calendar year */
  currentMonth: number | null
  formatUSD: (value: number) => string
}

export function CostSummaryCard({ year, monthSums, serviceCount, currentMonth, formatUSD }: CostSummaryCardProps) {
  const hasData = serviceCount > 0
  const total = monthSums.reduce((a, b) => a + b, 0)
  const peakIndex = monthSums.reduce((best, v, i) => (v > monthSums[best] ? i : best), 0)

  const currentIndex = currentMonth === null ? null : currentMonth - 1
  const current = currentIndex === null ? 0 : monthSums[currentIndex]
  const previous = currentIndex === null || currentIndex === 0 ? null : monthSums[currentIndex - 1]
  const change = previous === null
    ? null
    : previous === 0
      ? (current > 0 ? 100 : 0)
      : ((current - previous) / previous) * 100
  const down = change !== null && change < 0

  const maxBar = Math.max(...monthSums, 0.01)

  return (
    <div className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      <div className="grid grid-cols-1 border-b border-[#eef2f6] sm:grid-cols-[1fr_1fr_1.3fr]">
        <div className="border-b border-[#eef2f6] px-6 py-5 sm:border-b-0 sm:border-r">
          <div className="text-xs font-[550] text-slate-500">Total costs · {year}</div>
          <div className="mt-1.5 text-[27px] font-[650] tracking-[-0.02em] tabular-nums">{formatUSD(total)}</div>
          <div className="mt-[3px] text-xs text-slate-400">
            {hasData ? `${serviceCount} service${serviceCount === 1 ? '' : 's'}` : 'No usage recorded'}
          </div>
        </div>
        <div className="border-b border-[#eef2f6] px-6 py-5 sm:border-b-0 sm:border-r">
          <div className="text-xs font-[550] text-slate-500">Most expensive month</div>
          <div className="mt-1.5 text-[27px] font-[650] tracking-[-0.02em] tabular-nums">{formatUSD(monthSums[peakIndex])}</div>
          <div className="mt-[3px] text-xs text-slate-400">
            {hasData ? new Date(year, peakIndex).toLocaleString('default', { month: 'long' }) : '—'}
          </div>
        </div>
        <div className="px-6 py-5">
          <div className="text-xs font-[550] text-slate-500">Current month</div>
          <div className="mt-1.5 flex items-baseline gap-2.5">
            <span className="text-[27px] font-[650] tracking-[-0.02em] tabular-nums">
              {currentIndex === null ? '—' : formatUSD(current)}
            </span>
            {change !== null && (
              <span
                className={`rounded-full px-2 py-0.5 text-[11.5px] font-semibold tabular-nums ${
                  down ? 'bg-[#ecfdf5] text-[#059669]' : 'bg-[#fef2f2] text-[#dc2626]'
                }`}
              >
                {down ? '↓' : '↑'} {Math.abs(change).toFixed(1)}%
              </span>
            )}
          </div>
          {currentIndex !== null && previous !== null && (
            <div className="mt-[3px] text-xs text-slate-400">
              vs. {MONTH_LABELS[currentIndex - 1]} ({formatUSD(previous)})
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto px-6 pt-[18px] pb-4">
        <div className="flex h-[150px] min-w-[560px] items-end gap-2">
          {MONTH_LABELS.map((label, i) => {
            const value = monthSums[i]
            const isCurrent = i === currentIndex && hasData
            return (
              <div
                key={label}
                title={`${label} · ${formatUSD(value)}`}
                className="flex h-full flex-1 flex-col items-center justify-end gap-[5px]"
              >
                <span className="text-[10.5px] font-semibold text-slate-600 tabular-nums">
                  {value > 0 ? formatUSD(value) : ''}
                </span>
                <div
                  className="w-full max-w-11 rounded-t-[5px] rounded-b-sm transition-[height] duration-300"
                  style={{
                    height: value > 0 ? Math.max(6, Math.round((value / maxBar) * MAX_BAR_HEIGHT)) : 3,
                    background: value > 0 ? (isCurrent ? '#2563eb' : '#93c5fd') : '#e2e8f0',
                  }}
                />
                <span className={`text-[11px] ${isCurrent ? 'font-[650] text-[#2563eb]' : 'font-[450] text-slate-400'}`}>
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
